import React from 'react';
import { useSelector } from 'react-redux';

import { Input, Label, RadioGroup } from '@components/index';
import { NAME_MAX_LENGTH, NAME_MIN_LENGTH } from '@constants/generic';
import { invalidCharInNameRegex } from '@constants/regex';
import SelectDateInput from '@features/member-management/components/SelectDateInput';
import { isInuredOptions } from '@features/member-management/constants';
import {
  CustomField,
  UserProfileCustomFieldType
} from '@features/member-management/types';
import { AddMemberFormDataType, ValidationError } from '@types';

interface MemberMoreInfoProps {
  formData: AddMemberFormDataType;
  handleFormDataChange: (
    field: keyof AddMemberFormDataType,
    value: string
  ) => void;
  setError: (error: ValidationError) => void;
  error: ValidationError;
  containerRef: React.RefObject<HTMLElement>;
  onFocused: () => void;
}

const MemberMoreInfo = ({
  formData,
  handleFormDataChange,
  setError,
  error,
  containerRef,
  onFocused
}: MemberMoreInfoProps) => {
  const { orgConfig } = useSelector((state: any) => state.user);

  const {
    guardianName,
    admissionNumber,
    admissionDate,
    counsellorName,
    isInsured
  } = formData;

  const memberNameMaxLength =
    orgConfig?.member_name_length_max || NAME_MAX_LENGTH;
  const memberNameMinLength =
    orgConfig?.member_name_length_min || NAME_MIN_LENGTH;

  const shouldShowCounsellorName = !!orgConfig.member_custom_fields?.find(
    ({ key }: CustomField) => key === UserProfileCustomFieldType.COUNSELLOR_NAME
  );

  const shouldShowIsInsured = !!orgConfig.member_custom_fields?.find(
    ({ key }: CustomField) => key === UserProfileCustomFieldType.IS_INSURED
  );

  const onGuardianNameChange = (value: string) => {
    handleFormDataChange('guardianName', value);
  };

  const onAdmissionNumberChange = (value: string) => {
    setError({ ...error, admissionNumber: '' });
    handleFormDataChange('admissionNumber', value);
  };

  const onAdmissionDateChange = (date?: Date) => {
    handleFormDataChange(
      'admissionDate',
      date?.toLocaleDateString('en-CA') ?? ''
    );
  };

  const onClearAdmissionDate = () => onAdmissionDateChange();

  const onCounsellorNameChange = (value: string) => {
    handleFormDataChange('counsellorName', value);
  };

  const onInsuredChange = (value: string) => {
    handleFormDataChange('isInsured', value);
  };

  return (
    <div
      role="presentation"
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={onFocused}
      onClick={onFocused}
      onFocus={onFocused}
      className="grid w-full grid-cols-2 gap-4 rounded-xl bg-theme p-6 shadow-tableShadow"
    >
      <div>
        <Label label="Guardian Name" />
        <Input
          value={guardianName}
          handleInputChange={onGuardianNameChange}
          inputRegex={invalidCharInNameRegex}
          type="text"
          placeholder="Enter Name"
          maxLength={memberNameMaxLength}
          minLength={memberNameMinLength}
        />
      </div>
      <div>
        <Label label="Admission Number" />
        <Input
          value={admissionNumber}
          handleInputChange={onAdmissionNumberChange}
          type="text"
          placeholder="Enter admission number"
          maxLength={30}
          errorMessage={error.admissionNumber}
        />
      </div>
      <SelectDateInput
        modalTitle="Select Admission Date"
        label="Admission Date"
        datePickerLabel="__/__/__"
        handleDateSelect={onAdmissionDateChange}
        selectedDate={admissionDate}
        onClearEndDate={onClearAdmissionDate}
      />
      {shouldShowCounsellorName && (
        <div>
          <Label label="Counsellor Name" />
          <Input
            value={counsellorName}
            handleInputChange={onCounsellorNameChange}
            type="text"
            placeholder="Enter counsellor name"
            maxLength={30}
            errorMessage={error.counsellorName}
          />
        </div>
      )}
      {shouldShowIsInsured && (
        <div>
          <Label label="Is Insured" />
          <RadioGroup
            selectedValue={isInsured}
            options={isInuredOptions}
            handleChange={onInsuredChange}
          />
        </div>
      )}
    </div>
  );
};

export default MemberMoreInfo;
